import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { AuthUser, currentToken, onSessionChange, refreshSession } from '../lib/api';
import { CLIENT_ID, TAB_ID } from '../lib/client-id';

interface SessionState {
  user: AuthUser | null;
  /** Ainda verificando se há sessão (cookie de renovação). */
  checking: boolean;
  socket: Socket | null;
  connected: boolean;
}

const SessionContext = createContext<SessionState>({ user: null, checking: true, socket: null, connected: false });

export const useSession = () => useContext(SessionContext);
export const useUser = () => useContext(SessionContext).user!;

/**
 * Sessão do usuário e conexão em tempo real. Ao abrir a página, tenta renovar pelo cookie;
 * com usuário, conecta o socket (o token é pedido a cada conexão, então reconectar depois de
 * expirar usa um token novo).
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const off = onSessionChange(setUser);
    void refreshSession().finally(() => setChecking(false));
    return () => {
      off();
    };
  }, []);

  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      queryClient.clear();
      return;
    }
    const s = io({
      path: '/socket.io',
      transports: ['websocket'],
      auth: (cb) => void currentToken().then((token) => cb({ token, clientId: CLIENT_ID, tabId: TAB_ID })),
    });
    s.on('connect', () => {
      setConnected(true);
      // Pode ter perdido avisos enquanto estava desconectado: relê tudo.
      void queryClient.invalidateQueries();
    });
    // Token expirado: o servidor avisa e desconecta; reconecta na hora com o token renovado. Sem
    // setTimeout: com a janela minimizada o navegador atrasa os timers, e a conexão é o sinal de
    // vida da sessão de foco.
    let expired = false;
    s.on('auth.expired', () => {
      expired = true;
    });
    s.on('disconnect', (reason) => {
      setConnected(false);
      if (expired && reason === 'io server disconnect') {
        expired = false;
        s.connect();
      }
    });
    setSocket(s);
    return () => {
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [userId, queryClient]);

  const value = useMemo(() => ({ user, checking, socket, connected }), [user, checking, socket, connected]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Assina um aviso do servidor enquanto o componente estiver na tela. */
export function useSocketEvent<T = Record<string, unknown>>(event: string, handler: (data: T) => void): void {
  const { socket } = useSession();
  useEffect(() => {
    if (!socket) return;
    socket.on(event, handler as (d: unknown) => void);
    return () => {
      socket.off(event, handler as (d: unknown) => void);
    };
  }, [socket, event, handler]);
}
